import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  listDevices,
  listClientGroups,
  listActiveDevices,
  createDevice,
  updateDevice,
  deleteDevice,
  setDeviceWatch,
} from '../../services/adminDeviceService';
import {
  AdminDevice,
  ActiveDevice,
  ClientGroupOption,
  DeviceCreateRequest,
  DeviceUpdateRequest,
} from '../../types/adminDevice';

interface AdminDeviceState {
  devices: AdminDevice[];
  clientGroups: ClientGroupOption[];
  activeDevices: ActiveDevice[];
  loading: boolean;
  saving: boolean;
  activeLoading: boolean;
  error: string | null;
  actionMessage: string | null;
}

const initialState: AdminDeviceState = {
  devices: [],
  clientGroups: [],
  activeDevices: [],
  loading: false,
  saving: false,
  activeLoading: false,
  error: null,
  actionMessage: null,
};

export const fetchDevices = createAsyncThunk(
  'adminDevices/fetchDevices',
  async (adminApiKey: string) => listDevices(adminApiKey)
);

export const fetchClientGroups = createAsyncThunk(
  'adminDevices/fetchClientGroups',
  async (adminApiKey: string) => listClientGroups(adminApiKey)
);

export const fetchActiveDevices = createAsyncThunk(
  'adminDevices/fetchActiveDevices',
  async (adminApiKey: string) => listActiveDevices(adminApiKey)
);

export const createDeviceThunk = createAsyncThunk(
  'adminDevices/createDevice',
  async ({ payload, adminApiKey }: { payload: DeviceCreateRequest; adminApiKey: string }) =>
    createDevice(payload, adminApiKey)
);

export const updateDeviceThunk = createAsyncThunk(
  'adminDevices/updateDevice',
  async ({
    serialNo,
    payload,
    adminApiKey,
  }: {
    serialNo: string;
    payload: DeviceUpdateRequest;
    adminApiKey: string;
  }) => updateDevice(serialNo, payload, adminApiKey)
);

export const deleteDeviceThunk = createAsyncThunk(
  'adminDevices/deleteDevice',
  async ({ serialNo, adminApiKey }: { serialNo: string; adminApiKey: string }) => {
    await deleteDevice(serialNo, adminApiKey);
    return serialNo;
  }
);

export const toggleWatchThunk = createAsyncThunk(
  'adminDevices/toggleWatch',
  async ({
    serialNo,
    enabled,
    adminApiKey,
  }: {
    serialNo: string;
    enabled: boolean;
    adminApiKey: string;
  }) => setDeviceWatch(serialNo, enabled, adminApiKey)
);

const upsertDevice = (state: AdminDeviceState, device: AdminDevice) => {
  const index = state.devices.findIndex((d) => d.serialNo === device.serialNo);
  if (index >= 0) {
    state.devices[index] = device;
  } else {
    state.devices.push(device);
  }
};

const adminDeviceSlice = createSlice({
  name: 'adminDevices',
  initialState,
  reducers: {
    clearDeviceError: (state) => {
      state.error = null;
    },
    clearDeviceMessage: (state) => {
      state.actionMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch devices
      .addCase(fetchDevices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDevices.fulfilled, (state, action) => {
        state.loading = false;
        state.devices = action.payload;
      })
      .addCase(fetchDevices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load devices';
      })
      // Client groups
      .addCase(fetchClientGroups.fulfilled, (state, action) => {
        state.clientGroups = action.payload;
      })
      .addCase(fetchClientGroups.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to load client groups';
      })
      // Active devices
      .addCase(fetchActiveDevices.pending, (state) => {
        state.activeLoading = true;
        state.error = null;
      })
      .addCase(fetchActiveDevices.fulfilled, (state, action) => {
        state.activeLoading = false;
        state.activeDevices = action.payload;
      })
      .addCase(fetchActiveDevices.rejected, (state, action) => {
        state.activeLoading = false;
        state.error = action.error.message || 'Failed to load active devices';
      })
      // Create
      .addCase(createDeviceThunk.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.actionMessage = null;
      })
      .addCase(createDeviceThunk.fulfilled, (state, action) => {
        state.saving = false;
        upsertDevice(state, action.payload);
        state.actionMessage = `Device '${action.payload.serialNo}' created successfully`;
      })
      .addCase(createDeviceThunk.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || 'Failed to create device';
      })
      // Update
      .addCase(updateDeviceThunk.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.actionMessage = null;
      })
      .addCase(updateDeviceThunk.fulfilled, (state, action) => {
        state.saving = false;
        upsertDevice(state, action.payload);
        state.actionMessage = `Device '${action.payload.serialNo}' updated successfully`;
      })
      .addCase(updateDeviceThunk.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || 'Failed to update device';
      })
      // Delete
      .addCase(deleteDeviceThunk.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.actionMessage = null;
      })
      .addCase(deleteDeviceThunk.fulfilled, (state, action) => {
        state.saving = false;
        state.devices = state.devices.filter((d) => d.serialNo !== action.payload);
        state.actionMessage = `Device '${action.payload}' deleted successfully`;
      })
      .addCase(deleteDeviceThunk.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || 'Failed to delete device';
      })
      // Watch toggle
      .addCase(toggleWatchThunk.fulfilled, (state, action) => {
        upsertDevice(state, action.payload);
      })
      .addCase(toggleWatchThunk.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update watch status';
      });
  },
});

export const { clearDeviceError, clearDeviceMessage } = adminDeviceSlice.actions;
export default adminDeviceSlice.reducer;
